import {
  UploadFailedFilesModal,
  UploadProgressModal
} from '@business/workflows/ComponentUI/Dashboard';
import { FilesUploadModal } from '@business/workflows/ComponentUI/UploadFilesModal';
import { getFolderDetails } from '@ports/common/folderStateSlice';
import {
  getIsMediaUploadModalOpen,
  getSubscriberId,
  getToken,
  getUserNotificationData,
  setIsMediaUploadModalOpen,
  setUserNotificationData
} from '@ports/common/sessionStateSlice';
import { getUserData } from '@ports/common/userStateSlice';
import { getUploadFolder } from '@utils/helpers/lib/mediaType';
import {
  uploadMultiplePhotos,
  validateMultipleFiles
} from '@utils/helpers/s3Upload';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { UploadProgressToast } from './UploadProgressToast';

const GROUP_TOAST_ID = 'photo-upload-progress';

export const MediaUploadPersister = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const token = useSelector(getToken);
  const subscriberId = useSelector(getSubscriberId);
  const userDetails = useSelector(getUserData);
  const isOpenFilesModal = useSelector(getIsMediaUploadModalOpen);
  const userNotificationData = useSelector(getUserNotificationData);
  const folderDetails = useSelector(getFolderDetails);
  const [showUploadModal, setShowUploadModal] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string>('');
  const [failedFiles, setFailedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState({
    current: 0,
    total: 0
  });
  const uploadAbortRef = useRef<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (userNotificationData?.page === 'photos') {
      const current = Number(
        userNotificationData?.notificationData?.uploadIndex
      );
      const total = Number(userNotificationData?.notificationData?.uploadTotal);
      const iconUrl = userNotificationData?.notificationData?.thumbnailUrl;

      setUploadProgress({
        current,
        total
      });

      if (current >= total) {
        dispatch(
          setUserNotificationData({
            page: '',
            notificationData: null
          })
        );
      }

      if (!toast.isActive(GROUP_TOAST_ID)) {
        toast.success(
          <UploadProgressToast
            current={current}
            total={total}
            iconUrl={iconUrl}
          />,
          {
            toastId: GROUP_TOAST_ID,
            closeOnClick: true,
            hideProgressBar: true,
            icon: false,
            style: {
              padding: 0, // remove Toastify default padding
              background: 'transparent'
            }
          }
        );
      } else {
        toast.update(GROUP_TOAST_ID, {
          render: (
            <UploadProgressToast
              current={current}
              total={total}
              iconUrl={iconUrl}
            />
          ),
          type: 'success'
        });
      }
    }
  }, [userNotificationData]);

  const photoUploadHandler = async (files: File[]) => {
    if (showUploadModal && files.length === 1) {
      setShowUploadModal(false);
    }

    const validFiles: File[] = [];
    const invalidFiles: File[] = [];

    files.forEach(file => {
      const validation = validateMultipleFiles([file], 1);
      if (validation.valid) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file);
      }
    });

    if (invalidFiles.length > 0) {
      const failedFileNames = invalidFiles.map(f => f.name).join(' and ');
      const errorMsg = t('dashboard.unsupportedFormatMessage', {
        fileNames: failedFileNames
      });

      setUploadError(errorMsg);
      setFailedFiles(invalidFiles);

      toast.error(
        <div>
          <div className="font-medium mb-1">
            {t('dashboard.unsupportedFormat')}
          </div>
          <div className="text-xs">
            {t('dashboard.unsupportedFormatMessage', {
              fileNames: failedFileNames
            })}
          </div>
        </div>,
        {
          autoClose: false,
          onClick: () => setShowUploadModal(true)
        }
      );
      return;
    }

    if (validFiles.length === 0) return;

    const totalFiles = validFiles.length + invalidFiles.length;
    setUploadProgress({ current: 0, total: totalFiles });
    setIsUploading(true);

    const uploadFailedFiles: File[] = [];
    let successCount = 0;

    try {
      const idToken = token || '';

      if (!idToken) {
        throw new Error(t('dashboard.authenticationRequired'));
      }

      for (let i = 0; i < validFiles.length; i++) {
        if (uploadAbortRef.current) break;
        let folder = getUploadFolder(validFiles[i]);
        try {
          await uploadMultiplePhotos(
            [validFiles[i]],
            idToken,
            subscriberId,
            folder,
            () => {
              setUploadProgress({
                current: successCount + 1,
                total: totalFiles
              });
            },
            subscriberId,
            {
              userId: userDetails?.userId || '',
              folderId: folderDetails?.folderId || '',
              uploadIndex: (i + 1).toString(),
              uploadTotal: totalFiles.toString()
            }
          );

          successCount++;

          /* // Show progress toast with click handler only if more files to upload
          if (successCount < validFiles.length) {
            toast.success(
              t('dashboard.uploadProgress', {
                current: successCount,
                total: totalFiles
              }),
              {
                onClick: () => setIsUploading(true)
              }
            );
          } else {
            // Final file uploaded - no click handler
            toast.success(
              t('dashboard.uploadProgress', {
                current: successCount,
                total: totalFiles
              })
            );
          } */
        } catch (fileError: any) {
          console.error(`Failed to upload ${validFiles[i].name}:`, fileError);
          uploadFailedFiles.push(validFiles[i]);
        }
      }

      setIsUploading(false);
      setUploadProgress({ current: 0, total: 0 });

      if (invalidFiles.length > 0) {
        const failedFileNames = invalidFiles.map(f => f.name).join(' and ');
        const errorMsg = t('dashboard.unsupportedFormatMessage', {
          fileNames: failedFileNames
        });

        setUploadError(errorMsg);
        setFailedFiles(invalidFiles);

        toast.error(
          <div>
            <div className="font-semibold mb-1">
              {t('dashboard.uploadProgressPhotos', {
                current: successCount,
                total: totalFiles
              })}
            </div>
            <div className="font-medium mb-1">
              {t('dashboard.unsupportedFormat')}
            </div>
            <div className="text-xs">
              {t('dashboard.unsupportedFormatMessage', {
                fileNames: failedFileNames
              })}
            </div>
          </div>,
          {
            autoClose: false,
            onClick: () => setShowUploadModal(true)
          }
        );
      } else if (uploadFailedFiles.length > 0) {
        const failedFileNames = uploadFailedFiles
          .map(f => f.name)
          .join(' and ');
        const errorMsg = t('dashboard.uploadFailedMessage', {
          fileNames: failedFileNames
        });

        setUploadError(errorMsg);
        setFailedFiles(uploadFailedFiles);

        toast.error(
          <div>
            <div className="font-semibold mb-1">
              {t('dashboard.uploadProgressPhotos', {
                current: successCount,
                total: totalFiles
              })}
            </div>
            <div className="font-medium mb-1">
              {t('dashboard.uploadFailed')}
            </div>
            <div className="text-xs">
              {t('dashboard.uploadFailedMessage', {
                fileNames: failedFileNames
              })}
            </div>
          </div>,
          {
            autoClose: false,
            onClick: () => setShowUploadModal(true)
          }
        );
      }
    } catch (error: any) {
      setIsUploading(false);
      setUploadProgress({ current: 0, total: 0 });

      const totalFiles = validFiles.length + invalidFiles.length;
      const failedFileNames = validFiles
        .slice(successCount)
        .map(f => f.name)
        .join(' and ');
      const errorMsg = error.message || t('dashboard.failedToUpload');

      setUploadError(errorMsg);
      setFailedFiles([...validFiles.filter((_, i) => i >= successCount)]);

      toast.error(
        <div>
          <div className="font-semibold mb-1">
            {t('dashboard.uploadProgressPhotos', {
              current: successCount,
              total: totalFiles
            })}
          </div>
          <div className="font-medium mb-1">{t('dashboard.uploadFailed')}</div>
          <div className="text-xs">
            {failedFileNames
              ? t('dashboard.uploadFailedMessage', {
                  fileNames: failedFileNames
                })
              : ''}
            {error.message || t('dashboard.failedToUpload')}
          </div>
        </div>,
        {
          autoClose: false,
          onClick: () => setShowUploadModal(true)
        }
      );
    } finally {
      if (fileInputRef?.current) {
        fileInputRef.current.value = '';
      }
      uploadAbortRef.current = false;
    }
  };

  return (
    <>
      <FilesUploadModal
        isOpen={isOpenFilesModal}
        onClose={() => dispatch(setIsMediaUploadModalOpen(false))}
        onUpload={files => {
          dispatch(setIsMediaUploadModalOpen(false));
          photoUploadHandler(files);
        }}
      />

      <input
        type="file"
        accept="image/*,video/*"
        multiple
        ref={fileInputRef}
        id="common-photo-upload-input"
        style={{ display: 'none' }}
        onChange={e => {
          const files = Array.from(e.target.files || []);
          if (files.length > 0) {
            photoUploadHandler(files);
          }
        }}
      />

      {showUploadModal && (
        <UploadFailedFilesModal
          isOpen={showUploadModal}
          onClose={() => {
            setShowUploadModal(false);
            setUploadError('');
            setFailedFiles([]);
          }}
          onUpload={photoUploadHandler}
          errorMessage={uploadError}
          failedFiles={failedFiles}
          setShowUploadModal={setShowUploadModal}
        />
      )}

      {isUploading && (
        <UploadProgressModal
          isOpen={isUploading}
          current={uploadProgress.current}
          total={uploadProgress.total}
          onClose={() => setIsUploading(false)}
        />
      )}
    </>
  );
};
